import { Combobox } from '#/shared/ui/combobox'
import type { ComboboxOption } from '#/shared/ui/combobox'
import type { GeoCodeResponse } from '#/shared/api/generated/shipment/models'

import { useComboboxFreeze } from '../model/use-combobox-freeze'
import { useCitySearch } from '../model/use-city-search'

interface CityComboboxProps {
  value: string
  onValueChange: (value: string) => void
  onGeocodedSelect?: (geocoded: GeoCodeResponse) => void
  label?: string
  placeholder?: string
  error?: boolean
  disabled?: boolean
  className?: string
}

const ALWAYS_PASS_FILTER = () => true

function cityKey(result: GeoCodeResponse): string {
  return `${result.city}|${result.state}`
}

function dedupeCityState(results: GeoCodeResponse[]): GeoCodeResponse[] {
  const seen = new Set<string>()
  const out: GeoCodeResponse[] = []
  for (const result of results) {
    const key = cityKey(result)
    if (seen.has(key)) continue
    seen.add(key)
    out.push(result)
  }
  return out
}

export function CityCombobox({
  value,
  onValueChange,
  onGeocodedSelect,
  label,
  placeholder,
  error,
  disabled,
  className,
}: CityComboboxProps) {
  const freeze = useComboboxFreeze<GeoCodeResponse>(value)
  const live = useCitySearch(value, {
    enabled: freeze.isUserTyped && !freeze.isJustSelected && !disabled,
  })
  const items = dedupeCityState(freeze.pickItems(live.results))
  const options: ComboboxOption[] = items.map((result) => ({
    value: cityKey(result),
    label: result.city,
    tag: result.state,
  }))

  const handleValueChange = (next: string) => {
    freeze.onTyped(next)
    onValueChange(next)
  }

  const handleSelect = (option: ComboboxOption) => {
    freeze.freeze(option.label, items)
    const result = items.find((it) => cityKey(it) === option.value)
    if (result) onGeocodedSelect?.(result)
  }

  return (
    <Combobox
      labelSize="sm"
      label={label}
      placeholder={placeholder}
      options={options}
      value={value}
      onValueChange={handleValueChange}
      onSelect={handleSelect}
      filter={ALWAYS_PASS_FILTER}
      loading={live.isLoading}
      hideEmpty={live.needsMoreInput || !freeze.isUserTyped}
      hideToggle
      disabled={disabled}
      error={error}
      className={className}
      clearable
    />
  )
}
